import { useCallback } from 'react';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { useQueryParam } from '@occc-app/hooks/useQueryParam';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { deletePaginationParams } from '@occc-app/hooks/usePaginationParams';

export type UseAdvancedQueryParamParams<T> = {
  paramName: string;
  allowedValues?: T[];
  readValidator?: (value: unknown) => value is T;
  parse?: (value: string) => NonNullable<T>;
  format?: (value: NonNullable<T>) => string;
  defaultValue: T;
  beforeParamName?: string;
  afterParamName?: string;
};

export const useAdvancedQueryParam = <T>({
  paramName,
  allowedValues = [],
  readValidator,
  parse,
  format,
  defaultValue,
  beforeParamName,
  afterParamName,
}: UseAdvancedQueryParamParams<T>) => {
  const defaultReadValidator = useCallback(
    (value: unknown): value is T => {
      if (!allowedValues || allowedValues.length === 0) {
        return true;
      }

      return allowedValues.includes(value as T);
    },
    [allowedValues],
  );
  const validator = readValidator ?? defaultReadValidator;

  return useQueryParam<T>(
    (searchParams) => {
      const value = searchParams.get(paramName);

      if (!value) {
        return defaultValue;
      }
      return validator(value) ? (parse ? parse(value) : value) : defaultValue;
    },
    (value, searchParams) => {
      if (value !== null && value !== undefined && value !== '' && value !== defaultValue) {
        let valueToSave = value.toString();
        if (format) {
          valueToSave = format(value);
        }

        searchParams.set(paramName, valueToSave);
      } else {
        searchParams.delete(paramName);
      }

      deletePaginationParams(searchParams, beforeParamName, afterParamName);
    },
  );
};
